import { Button, Card, Form, Input, message, Modal, Switch, Tag } from 'antd';
import React from 'react';
import { useSelector } from 'react-redux';
import { selectUserState } from '../../store/selectors/user';
import { apiBlogAutomationGenerate } from '../../services/api/blog-automation';
import styles from './index.module.scss';

const { TextArea } = Input;

export interface GeneratePostFormState {
  topic: string;
  seoKeywords: string[];
  autoPublish: boolean;
}

function BlogAutomationPage() {
  const [generateForm] = Form.useForm<GeneratePostFormState>();
  const { token } = useSelector(selectUserState);
  const [loading, setLoading] = React.useState(false);
  const [keywordInput, setKeywordInput] = React.useState('');
  const [keywords, setKeywords] = React.useState<string[]>([]);
  const [modal, modalContext] = Modal.useModal();

  const handleAddKeyword = () => {
    if (keywordInput.trim() && !keywords.includes(keywordInput.trim())) {
      const newKeywords = [...keywords, keywordInput.trim()];
      setKeywords(newKeywords);
      generateForm.setFieldValue('seoKeywords', newKeywords);
      setKeywordInput('');
    }
  };

  const handleRemoveKeyword = (keyword: string) => {
    const newKeywords = keywords.filter((k) => k !== keyword);
    setKeywords(newKeywords);
    generateForm.setFieldValue('seoKeywords', newKeywords);
  };

  const handleKeywordInputKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddKeyword();
    }
  };

  const generatePostCallback = React.useCallback(
    async (values: GeneratePostFormState) => {
      if (token) {
        setLoading(true);
        try {
          const [response, status] = await apiBlogAutomationGenerate(
            {
              topic: values.topic,
              seoKeywords: keywords,
              autoPublish: values.autoPublish || false,
            },
            token,
          );

          if (status === 201 && response.adminEditorUrl) {
            modal.success({
              title: 'Blog Post Generated',
              content: 'Your blog post has been successfully generated.',
              onOk: () => {
                window.open(response.adminEditorUrl, '_blank');
                generateForm.resetFields();
                setKeywords([]);
              },
              onClose: () => {
                generateForm.resetFields();
                setKeywords([]);
              },
              okText: 'View Post',
              cancelText: 'Close',
            });
            message.success('Blog post generated successfully!');
            generateForm.resetFields();
            setKeywords([]);
          } else {
            message.error('Error generating blog post!');
          }
        } catch (error) {
          message.error('Error generating blog post!');
        } finally {
          setLoading(false);
        }
      }
    },
    [token, keywords, generateForm],
  );

  return (
    <div className={styles.container}>
      <Card bordered={false} title="Blog Automation" className={styles.card}>
        <Form
          form={generateForm}
          onFinish={generatePostCallback}
          layout="vertical"
          className={styles.form}
        >
          <Form.Item
            label="Topic"
            name="topic"
            rules={[
              {
                required: true,
                message: 'Please input blog post topic!',
                max: 512,
              },
            ]}
          >
            <TextArea
              placeholder="Enter the topic for your blog post..."
              rows={3}
            />
          </Form.Item>

          <Form.Item label="SEO Keywords" name="seoKeywords">
            <div className={styles.keywordSection}>
              <div className={styles.keywordInput}>
                <Input
                  placeholder="Type keyword and press Enter"
                  value={keywordInput}
                  onChange={(e) => {
                    setKeywordInput(e.target.value);
                  }}
                  onKeyPress={handleKeywordInputKeyPress}
                  suffix={
                    <Button
                      type="link"
                      size="small"
                      onClick={handleAddKeyword}
                      disabled={!keywordInput.trim()}
                    >
                      Add
                    </Button>
                  }
                />
              </div>
              <div className={styles.keywords}>
                {keywords.map((keyword, index) => (
                  <Tag
                    key={index}
                    closable
                    onClose={() => {
                      handleRemoveKeyword(keyword);
                    }}
                    className={styles.keywordTag}
                  >
                    {keyword}
                  </Tag>
                ))}
              </div>
            </div>
          </Form.Item>

          <Form.Item
            label="Auto Publish"
            name="autoPublish"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>

          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              loading={loading}
              className={styles.submitButton}
            >
              Generate Blog Post
            </Button>
          </Form.Item>
        </Form>
      </Card>
      {modalContext}
    </div>
  );
}

export default BlogAutomationPage;
